import React from "react";
import styled from "styled-components";
import { ToggleButtonGroup } from "@mui/material";
import { CompactColor, ExpressionReactContext, ExpressionRenderType } from "../Structure/ExpressionReactContext";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

const StyledExpressionPiece = styled("div")<{
    hasChild?: boolean;
    renderType: ExpressionRenderType;
    compactColor?: CompactColor;
}>`
    display: flex;
    align-items: center;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
			padding: 4px;
			font-size: 14px;
			font-weight: 600;
			
			line-height: 20px;
			color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
		
			${props.hasChild ? `flex: 1;` : ``};
		`
            : `
                padding: 0 2px;
                white-space: pre;
                
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 15px;
                letter-spacing: 0px;
                text-align: left;
                color: ${props.compactColor?.expression || UIStyleConstants.BLACK_COLOR};
            `}
    &:first-child {
        padding-left: 0;
    }
`;

export const ExpressionPiece = (props: { hasChild?: boolean; children?: AppGlueAny; paddingLeft?: string }) => {
    const context = React.useContext(ExpressionReactContext);
    return (
        <StyledExpressionPiece
            {...props}
            style={{ paddingLeft: props.paddingLeft }}
            renderType={context.renderType}
            compactColor={context.color}
        />
    );
};

const StyledExpressionLineDiv = styled.div<{
    renderType: ExpressionRenderType;
    compactColor?: CompactColor;
    color?: string;
}>`
    display: flex;
    align-items: center;
    clear: left;
    width: 100%;
    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                margin-bottom: 3px;
                font-size: 14px;
                font-weight: 600;
                
                line-height: 20px;
                color: ${props.color || UIStyleConstants.TEXT_COLOR_DEFAULT};
                flex-wrap: nowrap;
            `
            : `
                &,
                .MuiTypography-body1 {
                    
                    font-size: 12px !important;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 15px;
                    letter-spacing: 0px;
                    text-align: left;
                    color: ${props.compactColor?.expression || UIStyleConstants.TEXT_COLOR_DEFAULT};

                    white-space: pre;

                    .InlineEdit {
                        border-bottom: none !important;
                        padding: 0px;
                    }
                }
		
		`}
`;

export const ExpressionLineDiv = (props: { children?: AppGlueAny; color?: string }) => {
    const context = React.useContext(ExpressionReactContext);
    return (
        <StyledExpressionLineDiv
            {...props}
            renderType={context.renderType}
            compactColor={context.color}
        />
    );
};

const StyledBracketedDiv = styled("div")<{ hasChild?: boolean; renderType: ExpressionRenderType }>`
    display: flex;
    align-items: center;
    position: relative;

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
			margin-left: 6px;
			padding: 2px 4px;
			margin-right: 10px;
			font-size: 14px;
			font-weight: 600;
			
			line-height: 20px;
			color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
			${props.hasChild ? `flex: 1;` : ``}
            &::before {
                content: "";
                width: 4px;
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                border: 1px solid  ${UIStyleConstants.GRAY_COLOR};
                border-right: none;
                border-top-left-radius: 3px;
                border-bottom-left-radius: 3px;
            }
        
            &::after {
                content: "";
                width: 4px;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                border: 1px solid  ${UIStyleConstants.GRAY_COLOR};
                border-left: none;
                border-top-right-radius: 3px;
                border-bottom-right-radius: 3px;
            }
		`
            : `
			margin-left: 4px;
		    
		    font-style: normal;
		    font-weight: 500;
		    font-size: 12px;
		    line-height: 15px;
		    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
			padding: 0px 2px;
		`}
`;

export const BracketedDiv = (props: { hasChild?: boolean; children?: AppGlueAny }) => {
    const context = React.useContext(ExpressionReactContext);
    return (
        <StyledBracketedDiv
            {...props}
            renderType={context.renderType}
        />
    );
};

export const RuleChangeToggleButtonGroup = styled(ToggleButtonGroup)`
    && {
        .MuiToggleButton-root {
            border: 1px solid ${UIStyleConstants.GRAY_COLOR};
            box-sizing: border-box;
            width: 28px;
            height: 28px;
            border-radius: 4px;

            &:not(:last-child) {
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }

            &:not(:first-child) {
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
            }

            &.Mui-selected {
                background: ${UIStyleConstants.GRAY_COLOR};
            }

            &:hover {
                background: ${UIStyleConstants.BLUE_COLOR};
            }
        }
    }
`;
